/**
 * The releases API — `apps/api`, deployed at `api.bigbearbackup.com`.
 *
 * The portal already bundles `releases/*.md` at build time, so this is never
 * the only source of release notes: it exists so that a note edited after the
 * last portal build still shows up, and so the list keeps growing without a
 * redeploy. `useReleases` treats a failure here as a non-event for exactly
 * that reason.
 */

import type { Release, ReleaseSummary } from "@bigbear/shared";

import { API_BASE_URL } from "./apiBase";

/** `GET /v1/releases` — summaries, newest first. */
export async function fetchReleaseSummaries(
  signal?: AbortSignal,
): Promise<ReleaseSummary[]> {
  return getJson<ReleaseSummary[]>("/v1/releases", signal);
}

/**
 * `GET /v1/releases/latest` — the newest release and its body in one answer.
 *
 * Preferred over reading the list and then fetching its first entry: those are
 * two responses that can reflect two different moments, so the version the
 * list calls newest and the body fetched for it can disagree. This can't.
 */
export async function fetchLatestRelease(
  signal?: AbortSignal,
): Promise<Release | null> {
  return getRelease("/v1/releases/latest", signal);
}

/**
 * `GET /v1/releases/:version` — one release with its Markdown body.
 * Resolves to `null` for a version the API doesn't know, since "no such
 * release" is an ordinary answer here rather than a failure.
 */
export async function fetchRelease(
  version: string,
  signal?: AbortSignal,
): Promise<Release | null> {
  return getRelease(`/v1/releases/${encodeURIComponent(version)}`, signal);
}

async function getRelease(
  path: string,
  signal?: AbortSignal,
): Promise<Release | null> {
  const response = await request(path, signal);
  if (response.status === 404) return null;
  if (!response.ok)
    throw new Error(
      `Could not reach the releases API (HTTP ${response.status}).`,
    );
  return (await response.json()) as Release;
}

async function getJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await request(path, signal);
  if (!response.ok)
    throw new Error(
      `Could not reach the releases API (HTTP ${response.status}).`,
    );
  return (await response.json()) as T;
}

function request(path: string, signal?: AbortSignal): Promise<Response> {
  // `exactOptionalPropertyTypes` rejects passing `signal: undefined`, so an
  // absent signal means omitting the key rather than setting it.
  return fetch(`${API_BASE_URL}${path}`, signal ? { signal } : {});
}
